import { useEffect, useMemo } from 'react';
import type { ValueEditorProps } from '../types';
import { ValueSelector } from './ValueSelector';
import { ValSelector } from './ValSelector';
import { getTypeFromBson } from '../utils/formatQuery/utils';
import { getDateTimezoneFormat } from './getDateTimezoneFormat';

const regexFieldTypes = [
  'contains',
  'doesNotContain',
  'endsWith',
  'beginsWith',
  'doesNotBeginWith',
  'doesNotEndWith',
  'between',
  'notBetween',
  'in',
  'notIn',
];
const disabledFieldTypes = ['null', 'notNull', 'exist', 'notExist'];

export const ValueEditor = ({
  operator,
  value,
  handleOnChange,
  title,
  className,
  type,
  inputType,
  values,
  fieldData,
  disabled,
  testID,
  fieldType,
  handleTypeChange,
  ...props
}: ValueEditorProps) => {
  // This side effect blanks out the value if 1) the inputType is "number",
  // 2) the operator is not "between", "notBetween", "in", or "notIn", and
  // 3) the value contains a comma.
  useEffect(() => {
    if (
      inputType === 'number' &&
      !['between', 'notBetween', 'in', 'notIn'].includes(operator) &&
      typeof value === 'string' &&
      value.includes(',')
    ) {
      handleOnChange('');
    }
  }, [handleOnChange, inputType, operator, value]);

  // const [manualType, setManualType] = useState<string>();

  useEffect(() => {
    const getManualType = () => {
      if (regexFieldTypes.includes(operator)) return 'regex';
      if (inputType === 'number') return 'number';
      if (inputType === 'objectId') return 'objectId';
      if (type === 'checkbox' || type === 'switch') return 'boolean';
      if (type === 'select' || type === 'multiselect') return type;
      if (type === 'radio') return 'radio';
      return getTypeFromBson(inputType!);
    };
    handleTypeChange(getManualType());
  }, [type, inputType, operator]);

  const typeList = useMemo(() => {
    return [
      ...(type === 'radio'
        ? [
            {
              name: 'radio',
              label: 'Radio',
            },
          ]
        : type === 'select'
        ? [
            {
              name: 'select',
              label: 'Select',
            },
          ]
        : type === 'multiselect'
        ? [
            {
              name: 'multiselect',
              label: 'Multi Select',
            },
          ]
        : []),
      {
        name: 'text',
        label: 'Text',
      },
      {
        name: 'number',
        label: 'Number',
      },
      {
        name: 'boolean',
        label: 'Boolean',
      },
      {
        name: 'date',
        label: 'Date',
      },
      {
        name: 'objectId',
        label: 'ObjectId',
      },
      {
        name: 'regex',
        label: 'Regex',
      },
      {
        name: 'object',
        label: 'Object',
      },
      {
        name: 'array',
        label: 'Array',
      },
    ];
  }, [type]);

  const disabledField = useMemo(() => {
    return regexFieldTypes.includes(operator) || disabledFieldTypes.includes(operator);
  }, [operator]);

  useEffect(() => {
    if (fieldType === 'date') {
      handleOnChange(getDateTimezoneFormat(value, props.userTimezone));
    } else if (fieldType === 'text' && typeof value !== 'string') {
      handleOnChange('');
    } else if (fieldType === 'objectId') {
      handleOnChange(`${value}`);
    } else if (fieldType === 'regex') {
      handleOnChange(`${value}`);
    } else if (fieldType === 'number' && typeof value !== 'number') {
      handleOnChange(0);
    } else if (fieldType === 'boolean' && typeof value !== 'boolean') {
      handleOnChange(false);
    }
  }, [fieldType]);

  if (disabledFieldTypes.includes(operator)) {
    return <div style={{ flex: 1 }}></div>;
  }

  return (
    <>
      <ValueSelector
        {...props}
        // testID={testID}
        className="rule-fieldType"
        title="Type"
        handleOnChange={handleTypeChange}
        disabled={disabled || disabledField}
        value={fieldType}
        options={typeList}
      />
      <ValSelector
        {...props}
        title={title}
        className={className}
        values={values}
        value={value}
        fieldData={fieldData}
        disabled={disabled}
        operator={operator}
        type={fieldType}
        handleOnChange={handleOnChange}
        testID={testID}
      />
    </>
  );
};

ValueEditor.displayName = 'ValueEditor';
